/**
 * @component ry-modal
 * @category  overlay
 * @summary   Centered dialog with backdrop, focus trap, scroll lock, Escape close, and declarative trigger pairing via the `modal="id"` attribute on `<ry-button>`.
 *
 * Reference: Fomantic UI's `.ui.modal`. Sizes, basic (chromeless), and fullscreen
 * variants map directly. The declarative `modal="id"` hook lives on the *trigger*
 * (`<ry-button>`), not on the modal — that is the ry-ui convention.
 *
 * @attribute id          string?            ID used by `<ry-button modal="id">` triggers to open this modal. Required for declarative pairing.
 * @attribute title       string?            Header text rendered into `<h3>`. Optional — if absent the header still renders for the close button alignment.
 * @attribute size        'mini' | 'small' | 'large' | 'fullscreen' ? default=(unset: ~32rem)   Dialog max-width preset. `fullscreen` ignores width and pins the dialog to the viewport edges.
 * @attribute basic       boolean   default=false   Removes the dialog chrome (background, border, shadow) so consumers can place arbitrary content over the dimmer. Inverted-text variant.
 * @attribute persistent  boolean   default=false   Escape key and backdrop-click do NOT close. Only `el.close()` or an inner `[close]` trigger will close it. Use sparingly — accessibility prefers easy dismissal.
 *
 * @property  state       'open' | 'closed'    Mirrors `data-ry-state`. Read-only. Mutate via `open()` / `close()` / `toggle()`.
 *
 * @method    open()      Open the modal. Locks body scroll, traps focus, fires `ry:open`. No-op if already open.
 * @method    close()     Close the modal. Releases scroll lock, restores focus, fires `ry:close`. No-op if already closed.
 * @method    toggle()    Open if closed, close if open.
 *
 * @event ry:beforeopen
 *        detail: {}
 *        fires:  at the top of `open()` (user or programmatic), before scroll lock, focus capture, or any state change
 *        cancelable: preventDefault() keeps the modal closed; `ry:open` does not fire
 *
 * @event ry:open
 *        detail: {}
 *        fires:  after `open()` resolves and state becomes "open"
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of `close()` — Escape, backdrop click, `[close]` trigger, or `el.close()` — before any state change
 *        cancelable: preventDefault() keeps the modal open; `ry:close` does not fire. The unsaved-changes guard.
 *
 * @event ry:close
 *        detail: {}
 *        fires:  after `close()` resolves and state becomes "closed"
 *
 * @keyboard  Escape — closes the modal (unless `[persistent]`). Tab / Shift+Tab cycle focus within the dialog only.
 *
 * @state     open / closed — driven by `data-ry-state`. CSS transitions read this state via `@starting-style` for enter animation.
 *
 * @content   Children become the modal body. A child `<footer>` is lifted into the footer slot — everything else becomes the body. The `[close]` attribute on any descendant button closes the modal on click.
 *
 * @dom
 *   ry-modal[data-ry-state="open"|"closed"]
 *     > div.ry-modal__backdrop      [data-ry-target="backdrop"]
 *     > div.ry-modal__dialog        [data-ry-target="dialog"]   role="dialog" aria-modal="true"
 *       > div.ry-modal__header       [data-ry-target="header"]
 *         > h3.ry-modal__title
 *         > button.ry-modal__close   [data-ry-target="close"] [close]
 *       > div.ry-modal__body          [data-ry-target="body"]
 *       > div.ry-modal__footer       [data-ry-target="footer"]  (only if <footer> child was provided)
 *
 * @gotcha  Body scroll lock uses `data-ry-scroll-lock` on `<body>` + a `--ry-scrollbar-width` custom property. Nested modals would clobber the lock — by design, only open one modal at a time.
 *
 * @gotcha  The declarative `modal="id"` hook lives on the *trigger* (e.g. `<ry-button modal="x">`), not on `<ry-modal>`. The modal scans the document for triggers at setup time; triggers added later need a manual `modal.open()` call or re-running `connectedCallback` is not enough — keep all triggers in DOM before the modal mounts.
 *
 * @gotcha  `[persistent]` blocks Escape AND backdrop click. It does NOT block `el.close()` or `[close]`-attributed buttons — those are explicit and always work.
 *
 * @example basic
 * ```html
 * <ry-button modal="welcome">Open</ry-button>
 * <ry-modal id="welcome" title="Welcome">
 *   <p>Glad you're here.</p>
 *   <footer>
 *     <ry-button close>Close</ry-button>
 *     <ry-button variant="primary" close>OK</ry-button>
 *   </footer>
 * </ry-modal>
 * ```
 *
 * @example mini
 * ```html
 * <ry-button modal="confirm-small">Open mini</ry-button>
 * <ry-modal id="confirm-small" size="mini" title="Confirm">
 *   Are you sure?
 *   <footer><ry-button close>Cancel</ry-button><ry-button variant="primary" close>OK</ry-button></footer>
 * </ry-modal>
 * ```
 *
 * @example small
 * ```html
 * <ry-button modal="confirm-small-2">Open small</ry-button>
 * <ry-modal id="confirm-small-2" size="small" title="Confirm">
 *   Are you sure?
 *   <footer><ry-button close>Cancel</ry-button><ry-button variant="primary" close>OK</ry-button></footer>
 * </ry-modal>
 * ```
 *
 * @example large
 * ```html
 * <ry-button modal="report-large">Open large</ry-button>
 * <ry-modal id="report-large" size="large" title="Report">
 *   Long-form content fits here.
 *   <footer><ry-button close>Close</ry-button></footer>
 * </ry-modal>
 * ```
 *
 * @example fullscreen
 * ```html
 * <ry-button modal="editor-fs">Open fullscreen</ry-button>
 * <ry-modal id="editor-fs" size="fullscreen" title="Editor">
 *   Edge-to-edge editing surface.
 *   <footer><ry-button close>Close</ry-button></footer>
 * </ry-modal>
 * ```
 *
 * @example basic-no-chrome
 * ```html
 * <ry-button modal="hero-promo">Open chromeless</ry-button>
 * <ry-modal id="hero-promo" basic>
 *   <h1 style="color:white">Limited offer</h1>
 *   <ry-button variant="primary" close>Claim</ry-button>
 * </ry-modal>
 * ```
 *
 * @example persistent
 * ```html
 * <ry-button modal="agree">Open persistent</ry-button>
 * <ry-modal id="agree" title="Terms" persistent>
 *   You must accept to continue. Escape and backdrop click are blocked.
 *   <footer><ry-button variant="primary" close>I accept</ry-button></footer>
 * </ry-modal>
 * ```
 *
 * @tutorial open-and-close
 * ```js
 * const el = mount('<ry-modal id="t" title="X"><p>Body</p></ry-modal>');
 * await expectEqual(el.state, 'closed', 'starts closed');
 * await expectEvent(el, 'ry:open', () => el.open());
 * await expectEqual(el.state, 'open');
 * await expectEvent(el, 'ry:close', () => el.close());
 * await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial escape-closes
 * ```js
 * const el = mount('<ry-modal id="t"><p>Body</p></ry-modal>');
 * el.open();
 * await expectEqual(el.state, 'open');
 * const ev = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
 * document.dispatchEvent(ev);
 * await expectEqual(el.state, 'closed', 'escape closes the modal');
 * ```
 *
 * @tutorial backdrop-click-closes
 * ```js
 * const el = mount('<ry-modal id="t"><p>Body</p></ry-modal>');
 * el.open();
 * const backdrop = el.querySelector('[data-ry-target="backdrop"]');
 * backdrop.click();
 * await expectEqual(el.state, 'closed', 'backdrop click closes');
 * ```
 *
 * @tutorial persistent-blocks-escape-and-backdrop
 * ```js
 * // Wrong assumption: every modal closes on Escape. [persistent] modals do not.
 * // Document the exception so an agent does not write a test that expects close.
 * const el = mount('<ry-modal id="t" persistent><p>Body</p></ry-modal>');
 * el.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'open', 'escape ignored when persistent');
 * el.querySelector('[data-ry-target="backdrop"]').click();
 * await expectEqual(el.state, 'open', 'backdrop click ignored when persistent');
 * // el.close() still works — explicit always wins
 * el.close();
 * await expectEqual(el.state, 'closed', 'el.close() still works on persistent');
 * ```
 *
 * @tutorial inner-close-attribute
 * ```js
 * // The [close] attribute on any descendant button wires it to close the modal.
 * // This is what makes <footer><ry-button close>OK</ry-button></footer> "just work".
 * const el = mount(`
 *   <ry-modal id="t" title="X">
 *     <footer><button id="ok" close>OK</button></footer>
 *   </ry-modal>
 * `);
 * el.open();
 * el.querySelector('#ok').click();
 * await expectEqual(el.state, 'closed', 'inner [close] button closes the modal');
 * ```
 *
 * @tutorial toggle
 * ```js
 * const el = mount('<ry-modal id="t"><p>X</p></ry-modal>');
 * el.toggle(); await expectEqual(el.state, 'open');
 * el.toggle(); await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial open-is-idempotent
 * ```js
 * // Wrong assumption: open() fires ry:open every time it is called.
 * // It does NOT — calling open() on an already-open modal is a no-op.
 * const el = mount('<ry-modal id="t"><p>X</p></ry-modal>');
 * const openCount = [];
 * el.addEventListener('ry:open', () => openCount.push(1));
 * el.open(); el.open(); el.open();
 * await expectEqual(openCount.length, 1, 'open() is idempotent — emits ry:open once');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // The unsaved-changes guard: preventDefault() on ry:beforeclose keeps the
 * // modal open, whatever asked it to close (Escape, backdrop, el.close()).
 * const el = mount('<ry-modal id="t"><p>X</p></ry-modal>');
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @chain ry-button
 * ```js
 * // The declarative pairing — <ry-button modal="x"> opens <ry-modal id="x">.
 * // This is the highest-traffic ry-modal usage path.
 * // NOTE: mount() only appends the first root element, so wrap multi-root
 * // chain markup in a <div> wrapper.
 * const root = mount(`
 *   <div>
 *     <ry-button modal="demo" id="trigger">Open</ry-button>
 *     <ry-modal id="demo" title="Demo"><p>Hi</p></ry-modal>
 *   </div>
 * `);
 * const trigger = root.querySelector('#trigger');
 * const modal = root.querySelector('#demo');
 * await expectEqual(modal.state, 'closed');
 * trigger.click();
 * await expectEqual(modal.state, 'open', 'trigger opens paired modal');
 * ```
 *
 * @see ry-button
 * @see ry-drawer
 */

import { RyElement } from '../core/ry-element.js';

export class RyModal extends RyElement {
  #scrollbarWidth = 0;
  #restoreFocus: (() => void) | null = null;

  setup(): void {
    // Handle Option B: transform minimal markup
    this.#transformMinimalMarkup();

    // Find trigger buttons anywhere in document
    const modalId = this.id;
    if (modalId) {
      // data-ry-modal-trigger attribute
      document.querySelectorAll<HTMLElement>(`[data-ry-modal-trigger="${modalId}"]`).forEach(trigger => {
        this.on(trigger, 'click', () => this.open());
      });

      // modal="id" attribute on ry-button
      document.querySelectorAll<HTMLElement>(`ry-button[modal="${modalId}"], [modal="${modalId}"]`).forEach(trigger => {
        this.on(trigger, 'click', () => this.open());
      });
    }

    // Close buttons inside modal (use [close] attribute)
    this.$$('[close]').forEach(closeBtn => {
      this.on(closeBtn, 'click', () => this.close());
    });

    // Backdrop click (skipped when [persistent])
    const backdrop = this.$('[data-ry-target="backdrop"]');
    if (backdrop) {
      this.on(backdrop, 'click', () => {
        if (this.hasAttribute('persistent')) return;
        this.close();
      });
    }

    // Escape key
    this.on(document, 'keydown', this.#handleKeydown);

    // Calculate scrollbar width
    this.#scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Initialize state
    if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }
  }

  #transformMinimalMarkup(): void {
    // Check if already has full markup
    if (this.$('[data-ry-target="dialog"]')) return;

    const title = this.getAttribute('title') ?? '';
    const footerEl = this.$<HTMLElement>('footer');
    const footerContent = footerEl?.innerHTML ?? '';

    const bodyContent = Array.from(this.childNodes)
      .filter(n => !(n instanceof HTMLElement && n.tagName === 'FOOTER'))
      .map(n => n instanceof Element ? n.outerHTML : n.textContent)
      .join('');

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="backdrop" class="ry-modal__backdrop"></div>
      <div data-ry-target="dialog" class="ry-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="ry-modal-title-${this.id}">
        <div data-ry-target="header" class="ry-modal__header">
          <h3 class="ry-modal__title" id="ry-modal-title-${this.id}">${title}</h3>
          <button data-ry-target="close" class="ry-modal__close" close aria-label="Close">&times;</button>
        </div>
        <div data-ry-target="body" class="ry-modal__body">${bodyContent}</div>
        ${footerContent ? `<div data-ry-target="footer" class="ry-modal__footer">${footerContent}</div>` : ''}
      </div>
    `;
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (this.state !== 'open') return;
    if (this.hasAttribute('persistent')) return;
    this.close();
  };

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;

    // Capture focus to restore on close — shared helper from RyElement
    this.#restoreFocus = this.withReturnFocus();

    // Lock body scroll — REFERENCE-COUNTED via shared helper. Nested overlays
    // no longer clobber each other's locks.
    this.lockScroll();
    // Compat: existing CSS reads --ry-scrollbar-width
    document.body.style.setProperty('--ry-scrollbar-width', `${this.#scrollbarWidth}px`);

    // Open modal
    this.state = 'open';

    // Trap focus
    const dialog = this.$<HTMLElement>('[data-ry-target="dialog"]');
    if (dialog) {
      this.nextFrame().then(() => {
        this.trapFocus(dialog);
      });
    }

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;

    // Release one scroll-lock ref. Body lock fully releases only when count = 0.
    this.unlockScroll();
    if (!document.body.hasAttribute('data-ry-scroll-lock')) {
      document.body.style.removeProperty('--ry-scrollbar-width');
    }

    // Close modal
    this.state = 'closed';

    // Restore focus
    this.#restoreFocus?.();
    this.#restoreFocus = null;

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }
}

customElements.define('ry-modal', RyModal);
